import { useState, useEffect, useRef } from "react";
import {
  Hash, TagSimple, X, CaretUp, CaretDown, Toolbox, Package, MapPin,
  ArrowLeft, ArrowRight, ArrowClockwise, ArrowCounterClockwise, Spinner, Sliders,
} from "@phosphor-icons/react";
import { Slider } from "@/components/ui/slider";
import { EditableTextareaComponent } from "components/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { useUsers } from "shared/contexts/users.context";
import { SecondaryLabel } from "shared/types/file";
import TagSelector from "screens/contacts/components/tagSelector";
import UniversalPickerModal from "shared/components/UniversalPickerModal/UniversalPickerModal";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Building,
  Checkmark,
  Clock,
  Edit,
  Stack,
  Tag,
  Trash,
  User,
} from "assets/icons";

interface FullScreenViewProps {
  image: any;
  onClose: () => void;
  onDelete: (id: number) => void;
  onUpdate: (updatedImage: any) => void;
  onPrev?: () => void;
  onNext?: () => void;
  currentIndex?: number;
  totalInPage?: number;
}

const FullScreenView = ({ image, onClose, onDelete, onUpdate, onPrev, onNext, currentIndex, totalInPage }: FullScreenViewProps) => {
  const [editedImage, setEditedImage] = useState<any>(image);
  const [descriptionEditMode, setDescriptionEditMode] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  // Optimistic CSS turn shown while the server bakes the rotation into the file.
  const [previewRotation, setPreviewRotation] = useState(0);
  // Free-angle (e.g. 45°) rotation popover.
  const [rotatePopoverOpen, setRotatePopoverOpen] = useState(false);
  const [customAngle, setCustomAngle] = useState(0);
  const awaitingRotatedLoadRef = useRef(false);
  const rotateSafetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const descriptionRef = useRef(null);
  const { users } = useUsers();
  const { session } = useSession();

  const [isRenovationProjectPickerOpen, setIsRenovationProjectPickerOpen] = useState(false);
  const [isProjectPartPickerOpen, setIsProjectPartPickerOpen] = useState(false);

  useEffect(() => {
    setEditedImage(image);
    // Reset any in-progress rotation visuals when navigating to another photo.
    setPreviewRotation(0);
    setIsRotating(false);
    awaitingRotatedLoadRef.current = false;
  }, [image]);

  useEffect(() => {
    return () => {
      if (rotateSafetyTimerRef.current) clearTimeout(rotateSafetyTimerRef.current);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && onPrev) onPrev();
      if (e.key === "ArrowRight" && onNext) onNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    if (descriptionEditMode && descriptionRef.current) {
      (descriptionRef.current as any).focus?.();
    }
  }, [descriptionEditMode]);

  const getUserFullName = (img: any) => {
    const found = users?.find((u: any) => u.id == img?.createdBy);
    return found ? `${found.first_name} ${found.last_name}` : "Onbekende gebruiker";
  };

  const handleSaveSummary = async () => {
    try {
      await fetchWithAuth(`${API_URL}/file/edit`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editedImage),
      }, session);
      onUpdate(editedImage);
    } catch (error) {
      console.error("Error saving summary:", error);
      toast.error("Fout bij opslaan beschrijving");
    } finally {
      setDescriptionEditMode(false);
    }
  };

  const handleDeletePicture = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/file`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editedImage),
      }, session);
      if (res.ok) {
        onDelete(image.id);
        toast.success("Afbeelding werd verwijderd!");
      } else {
        toast.error("Fout bij verwijderen afbeelding (server error)");
      }
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Fout bij verwijderen afbeelding");
    } finally {
      setIsDeleting(false);
    }
  };

  // Rotate the photo by an arbitrary angle (90°, 45°, ...) and persist it (server bakes
  // it into the SharePoint file).
  const handleRotate = async (delta: number) => {
    if (isRotating || !editedImage?.id) return;
    setIsRotating(true);
    setPreviewRotation(delta); // optimistic: show the current image turned

    try {
      const res = await fetchWithAuth(
        `${API_URL}/file/rotate-image/${editedImage.id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ degrees: delta }),
        },
        session,
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || "Rotatie mislukt");
      }

      const data = await res.json();
      const updated = {
        ...editedImage,
        imageUrl: data.imageUrl || editedImage.imageUrl,
        thumbnailUrl: data.thumbnailUrl || editedImage.thumbnailUrl,
      };

      // The returned bytes are already rotated, so drop the CSS preview and let the
      // freshly-fetched image load behind the overlay (cleared on its onLoad).
      awaitingRotatedLoadRef.current = true;
      setEditedImage(updated);
      onUpdate(updated);
      setPreviewRotation(0);
      toast.success("Foto geroteerd en opgeslagen");

      // Safety net: if the new image URL is identical and onLoad never fires, still
      // release the overlay after a few seconds.
      if (rotateSafetyTimerRef.current) clearTimeout(rotateSafetyTimerRef.current);
      rotateSafetyTimerRef.current = setTimeout(() => {
        awaitingRotatedLoadRef.current = false;
        setIsRotating(false);
      }, 5000);
    } catch (error) {
      console.error("Error rotating image:", error);
      setPreviewRotation(0);
      setIsRotating(false);
      toast.error(error instanceof Error ? error.message : "Fout bij roteren van foto");
    }
  };

  const handleRotatedImageLoad = () => {
    if (awaitingRotatedLoadRef.current) {
      awaitingRotatedLoadRef.current = false;
      if (rotateSafetyTimerRef.current) clearTimeout(rotateSafetyTimerRef.current);
      setIsRotating(false);
    }
  };

  // Free-angle rotation popover: keep a live CSS preview in sync with the chosen angle.
  const handleRotatePopoverChange = (open: boolean) => {
    setRotatePopoverOpen(open);
    if (open) {
      setCustomAngle(0);
      setPreviewRotation(0);
    } else if (!isRotating) {
      setCustomAngle(0);
      setPreviewRotation(0);
    }
  };

  const handleCustomAngleChange = (angle: number) => {
    setCustomAngle(angle);
    setPreviewRotation(angle); // live preview while adjusting
  };

  const handleApplyCustomRotation = async () => {
    setRotatePopoverOpen(false);
    if (customAngle % 360 === 0) {
      setPreviewRotation(0);
      return;
    }
    const angle = customAngle;
    setCustomAngle(0);
    await handleRotate(angle);
  };

  const handleRemoveTag = async (tag: any) => {
    try {
      await fetchWithAuth(`${API_URL}/file/remove-secondary-tag`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileLogId: editedImage.id, tag }),
      }, session);
      const updated = {
        ...editedImage,
        secondaryLabels: editedImage.secondaryLabels.filter((l: any) => l !== tag),
      };
      setEditedImage(updated);
      onUpdate(updated);
    } catch (error) {
      console.error("Error removing tag:", error);
      toast.error("Fout bij verwijderen tag");
    }
  };

  const handleAddTag = async (tag: any) => {
    try {
      await fetchWithAuth(`${API_URL}/file/add-secondary-tag`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileLogId: editedImage.id, tag }),
      }, session);
      const updated = {
        ...editedImage,
        secondaryLabels: [...(editedImage.secondaryLabels || []), tag],
      };
      setEditedImage(updated);
      onUpdate(updated);
    } catch (error) {
      console.error("Error adding tag:", error);
      toast.error("Fout bij toevoegen tag");
    }
  };

  const handleSelectRenovationProject = async (project: any) => {
    try {
      const updated = {
        ...editedImage,
        renovationProjectId: project.id,
        renovationProject: project,
        projectPartId: null,
        projectPart: null,
      };
      await fetchWithAuth(`${API_URL}/file/edit`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updated),
      }, session);
      setEditedImage(updated);
      onUpdate(updated);
      setIsRenovationProjectPickerOpen(false);
      toast.success("Renovatieproject gekoppeld!");
    } catch (error) {
      console.error("Error linking renovation project:", error);
      toast.error("Fout bij koppelen van renovatieproject");
    }
  };

  const handleSelectProjectPart = async (part: any) => {
    try {
      const updated = { ...editedImage, projectPartId: part.id, projectPart: part };
      await fetchWithAuth(`${API_URL}/file/edit`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updated),
      }, session);
      setEditedImage(updated);
      onUpdate(updated);
      setIsProjectPartPickerOpen(false);
      toast.success("Projectonderdeel gekoppeld!");
    } catch (error) {
      console.error("Error linking project part:", error);
      toast.error("Fout bij koppelen van projectonderdeel");
    }
  };

  const handleRemoveRenovationProject = async () => {
    try {
      const updated = {
        ...editedImage,
        renovationProjectId: null, renovationProject: null,
        projectPartId: null, projectPart: null,
      };
      await fetchWithAuth(`${API_URL}/file/edit`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updated),
      }, session);
      setEditedImage(updated);
      onUpdate(updated);
      toast.success("Renovatieproject ontkoppeld");
    } catch (error) {
      console.error("Error removing renovation project:", error);
      toast.error("Fout bij ontkoppelen van renovatieproject");
    }
  };

  const handleRemoveProjectPart = async () => {
    try {
      const updated = { ...editedImage, projectPartId: null, projectPart: null };
      await fetchWithAuth(`${API_URL}/file/edit`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updated),
      }, session);
      setEditedImage(updated);
      onUpdate(updated);
      toast.success("Projectonderdeel ontkoppeld");
    } catch (error) {
      console.error("Error removing project part:", error);
      toast.error("Fout bij ontkoppelen van projectonderdeel");
    }
  };

  const updateDisplayOrder = async (newOrder: number) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/file/update-display-order/${image.id}`,
        { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ displayOrder: newOrder }) },
        session,
      );
      if (!response.ok) throw new Error("Failed to update display order");
      const updated = { ...editedImage, displayOrder: newOrder };
      setEditedImage(updated);
      onUpdate(updated);
      toast.success("Volgorde bijgewerkt");
    } catch (error) {
      console.error("Error updating display order:", error);
      toast.error("Kon volgorde niet bijwerken");
    }
  };

  // Badge helper
  const Badge = ({ children, className = "", onClick }: { children: React.ReactNode; className?: string; onClick?: () => void }) => (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${className}`}
      onClick={onClick}
    >
      {children}
    </span>
  );

  return (
    <TooltipProvider>
      <div className="fixed inset-0 z-[100] flex flex-col bg-white/90 backdrop-blur-sm">
        {/* Top bar */}
        <div className="flex justify-between p-3">
          <div className="flex flex-col gap-2 max-w-[60%]">
            <EditableTextareaComponent
              ref={descriptionRef}
              editMode={descriptionEditMode}
              value={editedImage.summary || ""}
              isLoading={false}
              onChange={(value: any) => setEditedImage((prev: any) => ({ ...prev, summary: value }))}
            />
            {/* Metadata badges */}
            <div className="flex flex-wrap gap-1.5">
              {(image.building?.id || image.buildingId) && (
                <Badge className="border-moss-primary text-moss-primary">
                  <Building className="h-3 w-3" />
                  {image.building?.id || image.buildingId}
                </Badge>
              )}
              {(image.unit?.id || image.unitId) && (
                <Badge className="border-moss-primary text-moss-primary">
                  <Stack className="h-3 w-3" />
                  {image.unit?.id || image.unitId}
                </Badge>
              )}
              {image.building?.googlePlaceLongAddress && (
                <Badge className="border-neutral-secondary-mid text-neutral-primary">
                  <MapPin size={12} weight="bold" />
                  {image.building.googlePlaceLongAddress}
                </Badge>
              )}
              {editedImage.renovationProject && (
                <Badge className="border-brick-primary bg-brick-primary text-white cursor-pointer"
                  onClick={() => setIsRenovationProjectPickerOpen(true)}>
                  <Toolbox size={12} weight="bold" />
                  {editedImage.renovationProject.title}
                  <button onClick={(e) => { e.stopPropagation(); handleRemoveRenovationProject(); }}
                    className="ml-0.5 hover:bg-white/20 rounded-full p-0.5">
                    <X size={10} weight="bold" />
                  </button>
                </Badge>
              )}
              {editedImage.projectPart && (
                <Badge className="border-moss-primary bg-moss-primary text-white cursor-pointer"
                  onClick={() => setIsProjectPartPickerOpen(true)}>
                  <Package size={12} weight="bold" />
                  {editedImage.projectPart.title}
                  <button onClick={(e) => { e.stopPropagation(); handleRemoveProjectPart(); }}
                    className="ml-0.5 hover:bg-white/20 rounded-full p-0.5">
                    <X size={10} weight="bold" />
                  </button>
                </Badge>
              )}
              {image.label?.[0] && (
                <Badge className="border-brick-primary bg-brick-primary text-white">
                  <Tag className="h-3 w-3" />
                  {image.label[0]}
                </Badge>
              )}
              {image.secondaryLabels?.map((label: string) => (
                <Badge key={label} className="border-moss-primary bg-moss-primary text-white">
                  <Tag className="h-3 w-3" />
                  {label}
                </Badge>
              ))}
              {image.createdAt && (
                <Badge className="border-neutral-secondary-mid text-neutral-primary">
                  <Clock className="h-3 w-3" />
                  {new Date(image.createdAt).toISOString().split("T")[0]}
                </Badge>
              )}
              {image.photoTakenDate && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Badge className="border-neutral-secondary-mid text-neutral-primary">
                        <Clock className="h-3 w-3" />
                        Foto: {new Date(image.photoTakenDate).toLocaleDateString("nl-BE")}
                      </Badge>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Datum waarop de foto genomen is (EXIF)</TooltipContent>
                </Tooltip>
              )}
              {image.locationDescription && (
                <Badge className="border-neutral-secondary-mid text-neutral-primary">
                  <MapPin size={12} weight="bold" />
                  {image.gpsCoordinates ? (
                    <a
                      href={`https://maps.google.com/?q=${image.gpsCoordinates.latitude},${image.gpsCoordinates.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {image.locationDescription}
                    </a>
                  ) : (
                    image.locationDescription
                  )}
                </Badge>
              )}
              {image.extractedData?.exif?.make && (
                <Badge className="border-neutral-secondary-mid text-neutral-primary">
                  {image.extractedData.exif.make} {image.extractedData.exif.model || ""}
                </Badge>
              )}
              {image.createdBy && (
                <Badge className="border-neutral-secondary-mid text-neutral-primary">
                  <User className="h-3 w-3" />
                  {getUserFullName(image)}
                </Badge>
              )}
              {image.id && (
                <Badge className="border-neutral-secondary-mid text-neutral-primary">
                  <Hash weight="bold" size={12} />
                  {image.id}
                </Badge>
              )}
              <span className="inline-flex items-center gap-1 rounded-full border border-neutral-secondary-mid px-2 py-0.5 text-xs font-medium text-neutral-primary">
                Volgorde: {editedImage.displayOrder || 10}
                <span className="flex flex-col">
                  <button onClick={() => updateDisplayOrder((editedImage.displayOrder || 10) + 1)}
                    className="p-0.5 hover:bg-neutral-primary/10 rounded-sm">
                    <CaretUp size={10} />
                  </button>
                  <button onClick={() => updateDisplayOrder(Math.max(1, (editedImage.displayOrder || 10) - 1))}
                    className="p-0.5 hover:bg-neutral-primary/10 rounded-sm">
                    <CaretDown size={10} />
                  </button>
                </span>
              </span>
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex items-start gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => {
                    if (descriptionEditMode) handleSaveSummary();
                    else setDescriptionEditMode(true);
                  }}
                  className={`rounded-full p-1.5 ${descriptionEditMode
                    ? "bg-moss-primary text-white"
                    : "border border-neutral-primary/30 text-neutral-primary"}`}
                >
                  {descriptionEditMode ? <Checkmark className="h-[18px] w-[18px]" /> : <Edit className="h-[18px] w-[18px]" />}
                </button>
              </TooltipTrigger>
              <TooltipContent>Wijzig beschrijving</TooltipContent>
            </Tooltip>
            <Popover modal={false}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <button className="rounded-full border border-neutral-primary/30 p-1.5 text-neutral-primary">
                      <TagSimple weight="duotone" size={18} />
                    </button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>Kies secundair label</TooltipContent>
              </Tooltip>
              <PopoverContent
                className="w-auto z-[110]"
                align="end"
                onOpenAutoFocus={(e) => e.preventDefault()}
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <header className="flex items-center gap-1.5 font-medium mb-2">
                  <TagSimple weight="duotone" size={18} />
                  <p className="text-sm">In welk kader zal deze foto worden gebruikt?</p>
                </header>
                <TagSelector
                  tags={Object.values(SecondaryLabel)}
                  selectedTags={editedImage.secondaryLabels || []}
                  handleTagClick={(tag: any) => {
                    if (editedImage.secondaryLabels?.includes(tag)) handleRemoveTag(tag);
                    else handleAddTag(tag);
                  }}
                />
              </PopoverContent>
            </Popover>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setIsRenovationProjectPickerOpen(true)}
                  className={`rounded-full p-1.5 ${editedImage.renovationProject
                    ? "bg-brick-primary text-white"
                    : "border border-neutral-primary/30 text-neutral-primary"}`}
                >
                  <Toolbox className="h-[18px] w-[18px]" weight="bold" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Koppel renovatieproject</TooltipContent>
            </Tooltip>
            {editedImage.renovationProject && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setIsProjectPartPickerOpen(true)}
                    className={`rounded-full p-1.5 ${editedImage.projectPart
                      ? "bg-moss-primary text-white"
                      : "border border-neutral-primary/30 text-neutral-primary"}`}
                  >
                    <Package className="h-[18px] w-[18px]" weight="bold" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Koppel projectonderdeel</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => handleRotate(-90)}
                  disabled={isRotating}
                  className="rounded-full border border-neutral-primary/30 p-1.5 text-neutral-primary hover:bg-neutral-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowCounterClockwise className="h-[18px] w-[18px]" weight="bold" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Draai 90° naar links</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => handleRotate(90)}
                  disabled={isRotating}
                  className="rounded-full border border-neutral-primary/30 p-1.5 text-neutral-primary hover:bg-neutral-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowClockwise className="h-[18px] w-[18px]" weight="bold" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Draai 90° naar rechts</TooltipContent>
            </Tooltip>
            <Popover open={rotatePopoverOpen} onOpenChange={handleRotatePopoverChange} modal={false}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <button
                      disabled={isRotating}
                      className="rounded-full border border-neutral-primary/30 p-1.5 text-neutral-primary hover:bg-neutral-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Sliders className="h-[18px] w-[18px]" weight="bold" />
                    </button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>Vrije rotatie (bv. 45°)</TooltipContent>
              </Tooltip>
              <PopoverContent align="end" className="w-72 z-[110]">
                <div className="flex flex-col gap-3">
                  <header className="flex items-center justify-between">
                    <p className="text-sm font-medium text-neutral-primary">Vrije rotatie</p>
                    <span className="text-sm font-semibold text-moss-primary">
                      {customAngle}°
                    </span>
                  </header>
                  <Slider
                    value={[customAngle]}
                    min={-180}
                    max={180}
                    step={1}
                    onValueChange={(v) => handleCustomAngleChange(v[0])}
                  />
                  <div className="flex flex-wrap gap-1.5">
                    {[-90, -45, 45, 90, 135, 180].map((deg) => (
                      <button
                        key={deg}
                        type="button"
                        onClick={() => handleCustomAngleChange(deg)}
                        className={`rounded-md border px-2 py-0.5 text-xs font-medium transition-colors ${
                          customAngle === deg
                            ? "border-moss-primary bg-moss-primary text-white"
                            : "border-neutral-primary/20 text-neutral-primary hover:bg-neutral-primary/5"
                        }`}
                      >
                        {deg > 0 ? `+${deg}` : deg}°
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-neutral-secondary-mid">
                    Bij niet-rechte hoeken worden de hoeken opgevuld (wit voor JPEG).
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => handleRotatePopoverChange(false)}
                      className="rounded-md border border-neutral-primary/20 px-3 py-1 text-xs font-medium text-neutral-primary hover:bg-neutral-primary/5"
                    >
                      Annuleer
                    </button>
                    <button
                      type="button"
                      onClick={handleApplyCustomRotation}
                      disabled={customAngle % 360 === 0}
                      className="rounded-md bg-moss-primary px-3 py-1 text-xs font-medium text-white hover:bg-moss-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Opslaan
                    </button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  disabled={isDeleting}
                  className="rounded-full border border-status-bad p-1.5 text-status-bad hover:bg-status-bad hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash className="h-[18px] w-[18px]" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Verwijder foto</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onClose}
                  className="rounded-full border border-neutral-primary/30 p-1.5 text-neutral-primary"
                >
                  <X size={18} weight="bold" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Sluiten</TooltipContent>
            </Tooltip>
          </div>
        </div>
        {/* Image with prev/next navigation */}
        <div className="relative flex flex-1 items-center justify-center overflow-auto p-4">
          {/* Previous button */}
          {onPrev && (
            <button
              onClick={onPrev}
              className="absolute left-4 z-10 rounded-full bg-black/30 p-3 text-white backdrop-blur-sm transition-colors hover:bg-black/50"
            >
              <ArrowLeft size={24} />
            </button>
          )}
          <img
            className="max-h-full max-w-full rounded-lg shadow-md"
            style={{
              transform: `rotate(${previewRotation}deg)`,
              transition: "transform 200ms ease",
            }}
            src={editedImage.imageUrl || editedImage.thumbnailUrl}
            alt="Gallery"
            onLoad={handleRotatedImageLoad}
          />
          {/* Overlay while the rotation is being saved to SharePoint */}
          {isRotating && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/40 backdrop-blur-[1px]">
              <div className="flex items-center gap-2 rounded-full bg-black/50 px-4 py-2 text-sm text-white">
                <Spinner size={18} className="animate-spin" />
                Foto wordt geroteerd...
              </div>
            </div>
          )}
          {/* Next button */}
          {onNext && (
            <button
              onClick={onNext}
              className="absolute right-4 z-10 rounded-full bg-black/30 p-3 text-white backdrop-blur-sm transition-colors hover:bg-black/50"
            >
              <ArrowRight size={24} />
            </button>
          )}
          {/* Counter */}
          {currentIndex != null && totalInPage != null && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-black/40 px-3 py-1 text-sm text-white backdrop-blur-sm">
              {currentIndex + 1} / {totalInPage}
            </div>
          )}
        </div>

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeletePicture}
        />

        <UniversalPickerModal
          isOpen={isRenovationProjectPickerOpen}
          mode="renovation-project"
          title="Selecteer renovatieproject"
          onClose={() => setIsRenovationProjectPickerOpen(false)}
          onConfirm={handleSelectRenovationProject}
          config={{ defaultValue: editedImage.renovationProject, allowRemoval: true }}
        />

        {editedImage.renovationProject && (
          <UniversalPickerModal
            isOpen={isProjectPartPickerOpen}
            mode="project-part"
            title="Selecteer projectonderdeel"
            onClose={() => setIsProjectPartPickerOpen(false)}
            onConfirm={handleSelectProjectPart}
            config={{
              currentRenovationProjectId: editedImage.renovationProject.id,
              defaultValue: editedImage.projectPart,
              allowRemoval: true,
            }}
          />
        )}
      </div>
    </TooltipProvider>
  );
};

export default FullScreenView;
